import * as Tabs from "@radix-ui/react-tabs";
import { Link } from "@tanstack/react-router";
import { Bell, Loader2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";

import { useUnreadCount } from "../../hooks/use-unread-count";
import { NotificationPreview } from "./components/notification-preview";
import { useNotificationDropdown } from "./hooks/use-notification-dropdown";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { m } from "@/paraglide/messages.js";

export function NotificationDropdown() {
  const contentRef = useRef<HTMLDivElement>(null);
  const handleOpenAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
    contentRef.current?.focus({ preventScroll: true });
  }, []);
  const { count, reset } = useUnreadCount();
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const {
    items,
    loading,
    error: loadError,
    refetch,
    markingAllRead,
    handleMarkAllRead,
    handleMarkRead,
  } = useNotificationDropdown(filter);
  const onMarkAllRead = async () => {
    const succeeded = await handleMarkAllRead();
    if (succeeded) reset();
    else toast.error(m.notifications_update_error());
  };

  const unreadItems = items.filter((n) => !n.readAt);
  const hasUnread = count > 0 || unreadItems.length > 0;
  const visibleItems = filter === "unread" ? unreadItems : items;
  const onMarkRead = async (id: string) => {
    if (!(await handleMarkRead(id))) toast.error(m.notifications_update_error());
  };
  const overflowCount = Math.max(0, count - unreadItems.length);
  const totalBadge = count;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={m.notifications_title() + (totalBadge > 0 ? ` (${totalBadge})` : "")}
          className="relative size-8 rounded-md text-muted-foreground hover:text-foreground"
          data-tour="notifications-bell"
        >
          <Bell className="h-4 w-4" />
          {totalBadge > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
              {totalBadge > 99 ? "99+" : totalBadge}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        tabIndex={-1}
        onOpenAutoFocus={handleOpenAutoFocus}
        align="end"
        aria-label={m.notifications_title()}
        className="w-[380px] max-w-[calc(100vw-24px)] overflow-hidden rounded-lg border-border bg-popover p-0 shadow-xl outline-none"
        sideOffset={8}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold">{m.notifications_title()}</h4>
            {totalBadge > 0 && (
              <span className="flex h-4 min-w-4 items-center justify-center rounded-sm bg-muted px-1.5 text-[11px] font-medium text-muted-foreground">
                {totalBadge}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {hasUnread && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-[11px] font-normal text-muted-foreground hover:text-foreground"
                disabled={markingAllRead}
                onClick={onMarkAllRead}
              >
                {m.notifications_read_all()}
              </Button>
            )}
          </div>
        </div>

        <Tabs.Root value={filter} onValueChange={(value) => setFilter(value === "unread" ? "unread" : "all")}>
          <Tabs.List aria-label={m.notifications_title()} className="flex gap-5 border-b border-border px-4">
            <Tabs.Trigger
              value="all"
              className="cursor-pointer border-b border-transparent py-2.5 text-xs text-muted-foreground outline-offset-4 data-[state=active]:border-foreground data-[state=active]:text-foreground"
            >
              {m.notifications_filter_all()}
            </Tabs.Trigger>
            <Tabs.Trigger
              value="unread"
              className="cursor-pointer border-b border-transparent py-2.5 text-xs text-muted-foreground outline-offset-4 data-[state=active]:border-foreground data-[state=active]:text-foreground"
            >
              {m.notifications_filter_unread()}{" "}
              {count > 0 && <span className="ml-1.5 text-[10px] tabular-nums">{count}</span>}
            </Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content
            value={filter}
            className="max-h-[min(440px,60vh)] overflow-y-auto overscroll-contain outline-none"
          >
            {loadError ? (
              <div role="alert" className="space-y-3 px-4 py-8 text-center text-xs text-muted-foreground">
                <p>{m.notifications_load_error()}</p>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  {m.notifications_retry()}
                </Button>
              </div>
            ) : loading && items.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : visibleItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
                  <Bell className="h-4 w-4" />
                </div>
                <p className="text-sm font-medium text-foreground">
                  {filter === "unread" ? m.notifications_all_caught_up() : m.notifications_empty_title()}
                </p>
                <p className="max-w-60 text-center text-xs">{m.notifications_empty_hint()}</p>
              </div>
            ) : (
              <div className="py-1">
                <div className="space-y-1 px-1.5 py-1">
                  {visibleItems.map((notification) => (
                    <NotificationPreview key={notification.id} notification={notification} onMarkRead={onMarkRead} />
                  ))}
                </div>
                {filter === "unread" && overflowCount > 0 && (
                  <Link
                    to="/notifications"
                    className="flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  >
                    {m.notifications_more_unread({ count: overflowCount })}
                  </Link>
                )}
              </div>
            )}
          </Tabs.Content>
        </Tabs.Root>
        <div className="border-t border-border px-4 py-3">
          <Link
            to="/notifications"
            className="flex items-center justify-between rounded-md text-xs text-muted-foreground hover:text-foreground"
          >
            {m.notifications_view_all()}
            <span aria-hidden>→</span>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
